import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  Box,
  Stack,
  Chip,
  IconButton,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { FaArrowLeft, FaCheck, FaEdit } from "react-icons/fa";
import { API_URL } from "../config";

export default function AdminEditExamPage() {
  const navigate = useNavigate();
  const { examId } = useParams();

  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isPostUTME, setIsPostUTME] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch exam details on mount
  useEffect(() => {
    fetch(`${API_URL}/admin/exam/${examId}/`)
      .then((res) => res.json())
      .then((data) => {
        setName(data.name || "");
        setImageUrl(data.exam_img || null);
        setIsPostUTME(data.is_postUTME || false);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load exam:", err);
        alert("Failed to load exam details.");
        setLoading(false);
      });
  }, [examId]);

  const updateExam = async () => {
    if (!name.trim()) {
      alert("Exam name is required.");
      return;
    }

    setSaving(true);
    try {
      await fetch(`${API_URL}/admin/exam/${examId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          is_postUTME: isPostUTME,
        }),
      });
      navigate(-1);
    } catch (err) {
      console.error("Update failed:", err);
      alert("Failed to update exam. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", p: 3, textAlign: "center" }}>
        <Typography>Loading exam details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, sm: 3 } }}>
      {/* Back Button */}
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        <FaArrowLeft />
      </IconButton>

      <Typography variant="h4" align="center" fontWeight="600" gutterBottom>
        Edit Exam
      </Typography>

      {/* Main Card */}
      <Card elevation={6}>
        <CardContent sx={{ pb: 2 }}>
          <Stack spacing={4}>
            {/* Exam Name */}
            <TextField
              label="Exam Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="e.g., JAMB 2024 Mathematics"
              InputProps={{
                startAdornment: <FaEdit sx={{ color: "text.secondary", mr: 1 }} />,
              }}
            />

            {/* Post-UTME Toggle */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPostUTME}
                    onChange={(e) => setIsPostUTME(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1" fontWeight="medium">
                    This is a Post-UTME Exam
                  </Typography>
                }
                labelPlacement="start"
                sx={{ ml: 0, width: "100%", justifyContent: "space-between" }}
              />
              {isPostUTME ? (
                <Chip
                  label="Post-UTME"
                  color="secondary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              ) : (
                <Chip label="Regular Exam" color="default" size="small" sx={{ mt: 1 }} />
              )}
            </Paper>

            {/* Existing Image Preview */}
            {imageUrl && (
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Current Exam Image
                </Typography>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxWidth: 500,
                    mx: "auto",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Current exam"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Image cannot be changed here. To update the image, delete and recreate the exam.
                </Alert>
              </Box>
            )}
          </Stack>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-end", p: 3, pt: 0 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<FaCheck />}
            onClick={updateExam}
            disabled={saving || !name.trim()}
          >
            {saving ? "Saving..." : "Update Exam"}
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
}